import { 
  collection, 
  addDoc, 
  onSnapshot, 
  query, 
  orderBy,
  Timestamp,
  limit,
  deleteDoc,
  doc,
  getDocs,
  writeBatch
} from 'firebase/firestore';
import { db } from './firestore';
import { ActivityLog } from '@/types';

export const getActivityLogsRef = (userId: string) => 
  collection(db, 'users', userId, 'activityLogs');

export const addActivityLog = async (
  userId: string, 
  action: ActivityLog['action'], 
  description: string,
  details?: string
) => {
  const ref = getActivityLogsRef(userId);
  await addDoc(ref, {
    action,
    description,
    details: details || null,
    timestamp: Timestamp.now()
  });
};

export const deleteActivityLog = async (userId: string, logId: string) => {
  const docRef = doc(db, 'users', userId, 'activityLogs', logId);
  await deleteDoc(docRef);
};

export const deleteMultipleActivityLogs = async (userId: string, logIds: string[]) => {
  const batch = writeBatch(db);
  logIds.forEach(logId => {
    const docRef = doc(db, 'users', userId, 'activityLogs', logId);
    batch.delete(docRef);
  });
  await batch.commit();
};

export const deleteAllActivityLogs = async (userId: string) => {
  const ref = getActivityLogsRef(userId);
  const snapshot = await getDocs(ref);
  const batch = writeBatch(db);
  snapshot.docs.forEach(docSnap => {
    batch.delete(docSnap.ref);
  });
  await batch.commit();
};

// Delete logs older than 1 month
export const deleteOldActivityLogs = async (userId: string, daysOld = 30) => {
  const ref = getActivityLogsRef(userId);
  const cutoffDate = new Date();
  cutoffDate.setDate(cutoffDate.getDate() - daysOld);
  
  const snapshot = await getDocs(ref);
  const batch = writeBatch(db);
  let deleteCount = 0;
  
  snapshot.docs.forEach(docSnap => {
    const data = docSnap.data();
    const logDate = data.timestamp?.toDate?.() || new Date(data.timestamp);
    
    if (logDate < cutoffDate) {
      batch.delete(docSnap.ref);
      deleteCount++;
    }
  });
  
  if (deleteCount > 0) {
    await batch.commit();
  }
  
  return deleteCount;
};

export const subscribeToActivityLogs = (
  userId: string,
  callback: (logs: ActivityLog[]) => void,
  onError?: (error: unknown) => void,
  logLimit = 100
) => {
  const ref = getActivityLogsRef(userId);
  const q = query(ref, orderBy('timestamp', 'desc'), limit(logLimit));
  
  return onSnapshot(
    q,
    (snapshot) => {
      const logs = snapshot.docs.map((docSnap) => ({
        id: docSnap.id,
        ...docSnap.data(),
        timestamp: (docSnap.data() as any).timestamp?.toDate?.()?.toISOString() || 
                   (docSnap.data() as any).timestamp,
      })) as ActivityLog[];
      callback(logs);
    },
    (error) => onError?.(error)
  );
};
